/**
 * @file This file contains the NPCManager class, responsible for managing
 * the spawning, movement, and state of NPCs (Non-Player Characters) on the game grid.
 * It also handles interactions between NPCs, such as proximity-based chats.
 * @module NPCManager
 */
import { npcRandomActionsConfig } from '../config/npcRandomActionsConfig.js';
 
import { Character } from '../models/Character.js';
import { npcIdleActionsConfig } from '../config/npcIdleActionsConfig.js';
import { npcActionsConfig } from '../config/npcActionsConfig.js';
import { nicknamesConfig } from '../config/nicknamesConfig.js';
import { agingConfig } from '../config/agingConfig.js';
import { skinTones } from '../config/skinToneConfig.js';

/**
 * Manages a specific type of NPCs (e.g., humans, non-humans), handling their
 * lifecycle including spawning, pathfinding, state updates, and persistence.
 *
 * @export
 * @class NPCManager
 * @typedef {NPCManager}
 */
export class NPCManager {
    /**
     * @param {object} dependencies - The dependencies for the NPCManager.
     * @param {import('./UIManager.js').UIManager} ui - The UI manager instance.
     * @param {import('./DebugManager.js').DebugManager} debug - The debug manager instance.
     * @param {object} npcTypeConfig - Configuration specific to this NPC type (human or non-human).
     * @param {import('../Game.js').Game} game - The main game instance.
     * @param {import('./AudioManager.js').AudioManager} audioManager - The audio manager instance.
     * @param {Array<object>} npcConfigs - An array of specific NPC configurations for this type.
     * @param {import('../models/Grid.js').Grid} grid - The game grid instance.
     * @param {object} masterItemsConfig - The master list of all item definitions.
     * @param {object} gameConfig - The active game configuration.
     * @param {import('./PreferencesManager.js').PreferencesManager} preferencesManager - The preferences manager instance.
     * @param {import('./SettlementManager.js').SettlementManager} settlementManager - The settlement manager instance.
     * @param {boolean} isDemoRunning - Flag to indicate if the game is in demo mode.
     * @param {import('./ObstacleManager.js').ObstacleManager} dependencies.obstacleManager - The obstacle manager instance.
     */
    constructor({ ui, debug, npcTypeConfig, game, npcConfigs, masterItemsConfig, audioManager, gameConfig, grid, preferencesManager, settlementManager, obstacleManager, isDemoRunning }) {
        /** @private */
        this.ui = ui;
        /** @private */
        this.debugManager = debug;
        /** @private */
        this.preferencesManager = preferencesManager;
        /** @private @type {import('../models/Grid.js').Grid|null} */
        this.grid = grid;

        /** @private */
        this.game = game; // Add reference to the main game instance
        /** @private */
        this.npcTypeConfig = npcTypeConfig;
        /** @private */
        this.npcClass = Character; // The class used to instantiate NPCs
        /** @private */
        this.audioManager = audioManager;
        /** @private */
        this.gameConfig = gameConfig;
        /** @private */
        this.npcConfigs = npcConfigs;
        /** @private */
        this.masterItemsConfig = masterItemsConfig;
        /** @private */
        this.settlementManager = settlementManager;
        /** @private */
        this.obstacleManager = obstacleManager;
        /** @private */
        this.isDemoRunning = isDemoRunning;
        /** @private @type {Map<number, Character>} */
        this.npcs = new Map();
        /** @private @type {number} */
        this.nextNpcId = 0;
        /** @private @type {boolean} */
        this.isPaused = false;
        /** @private @type {Array<{respawnTime: number}>} */
        this.respawnQueue = [];
        /** @private @type {Map<number, Character>} */
        this.exInhabitants = new Map();
        /**
         * The number of NPCs currently within the playable grid area.
         * This is used for stat calculations and must only count NPCs in the playable area.
         */
        this.npcsInPlayableGridCount = 0;

        this.debugManager.log(`NPCManager for ${this.npcTypeConfig.type} initializeASd.`);

        this.RESPAWN_DELAY_MS = 5000; // 5 seconds
        this.LEAVE_CHANCE = 0.05; // 5% chance per move when at an edge
        this.SWAP_CHANCE = 0.10; // 50% chance to swap accessories on interaction
        this.LOVE_CHANCE = 0.25; // 25% chance to fall in love on interaction
        this.GIFT_CHANCE = 0.10; // 30% chance to give an item to an empty-handed NPC
        this.PLAY_FIGHT_CHANCE = 0.20; // 20% chance for a play fight if both have weapons
        this.CHAT_SOUND_CHANCE = 0.1; // 50% chance to play a sound when NPCs chat
        this.HUNGER_THRESHOLD = 500; // Ticks before an NPC gets hungry and seeks food

        this.agingConfig = agingConfig;

           /**
             * The global probability that any zany action will be considered.
             * This is checked each time an NPC makes a free move decision.
             * @type {number}
             */
        this.globalProbability= 0.05;// 5% chance to consider a zany action
    }

    /**
     * Initializes or resets the NPC manager, spawning initial NPCs.
     */
    initManager() {
        this.npcs.clear();
        this.nextNpcId = 0;
        this.respawnQueue = [];
        this.exInhabitants.clear();

        if (this.npcTypeConfig.isHuman && this.gameConfig.isSettlersModeEnabled) {
            // --- Settlers Mode: Spawn 2 adults and 2 babies ---
            const adults = this.npcConfigs.filter(c => c.isAdult);
            const babies = this.npcConfigs.filter(c => c.isBaby);

            if (adults.length > 0) {
                this.spawnNpc(adults[Math.floor(Math.random() * adults.length)]);
                this.spawnNpc(adults[Math.floor(Math.random() * adults.length)]);
            }
            if (babies.length > 0) {
                this.spawnNpc(babies[Math.floor(Math.random() * babies.length)]);
                this.spawnNpc(babies[Math.floor(Math.random() * babies.length)]);
            }
            this.debugManager.log(`NPCManager for ${this.npcTypeConfig.type} initialized for Settlers Mode.`);

        } else {
            // --- Default Spawning Logic ---
            const numToSpawn = this.npcTypeConfig.maxSpawns || this.npcConfigs.length;
            let toSpawn = Math.min(numToSpawn, this.npcConfigs.length);
            if (this.npcTypeConfig.isHuman) {
                toSpawn = this.grid.size; // For human NPCs, spawn one per grid row/col (simplified)
            }
            for (let i = 0; i < toSpawn; i++) {
                this.spawnNpc();
            }
            this.debugManager.log(`NPCManager for ${this.npcTypeConfig.type} initialized with ${toSpawn} NPCs.`);
        }
    }

    /**
     * Spawns a single NPC on a random grid position.
     * @param {object} [specificConfig] - Optional. A specific NPC config to use instead of a random one.
     */
    spawnNpc(specificConfig) {
        if (this.npcConfigs.length === 0 || !this.grid) return;

        // Enforce a population cap to prevent overcrowding.
        const maxNpcs = Math.floor((this.grid.playableSize * this.grid.playableSize) / 5);
        if (this.npcs.size >= maxNpcs) {
            this.debugManager.log(`NPC spawn blocked: population cap of ${maxNpcs} reached.`);
            return;
        }

        // 50/50 chance to respawn an ex-inhabitant or create a new one.
        if (this.exInhabitants.size > 0 && Math.random() < 0.5) {
            const exInhabitantIds = Array.from(this.exInhabitants.keys());
            const randomId = exInhabitantIds[Math.floor(Math.random() * exInhabitantIds.length)];
            const npc = this.exInhabitants.get(randomId);
            this.exInhabitants.delete(randomId);

            // Reset state for re-entry
            npc.position = this._getRandomBufferPosition();
            npc.state.isNew = true;
            npc.state.isLeaving = false;
            npc.movement.path = [];

            this.npcs.set(npc.id, npc);
            this.debugManager.log(`Ex-inhabitant ${npc.id} (${npc.attributes.nickname}) has returned to the village.`);
            return; // The ex-inhabitant is back in the world, our job is done.
        } else {
            let config; // Declare config here to be accessible in this scope.
            if (specificConfig) {
                config = specificConfig;
            } else {
                const spawnableNpcs = this.npcConfigs.filter(c => c.canSpawn !== false);
                config = spawnableNpcs[Math.floor(Math.random() * spawnableNpcs.length)];
            }
            const startPos = this._getRandomBufferPosition();
            if (!startPos) return;

            const npc = this._createNpc(this.nextNpcId++, config, startPos);
            this.npcs.set(npc.id, npc);
            this.debugManager.log(`Spawned ${this.npcTypeConfig.type} ${npc.id} (${npc.config.name}) at (${npc.position.r}, ${npc.position.c}).`);
        }
    }

    /**
     * The main update loop, called every tick by the game engine.
     */
    update() {
        // Do nothing if the grid isn't ready or the manager is paused.
        this.npcsInPlayableGridCount = 0;
        if (!this.grid || this.isPaused) return;

        // Process each NPC individually.
        for (const npc of this.npcs.values()) {
            this._processSingleNpc(npc);
        }

        // For human NPCs, ensure a minimum population to keep the village active.
        // This check should not apply to non-human creatures.
        if (this.npcTypeConfig.isHuman) {
            if (this.npcs.size < this.grid.size && this.respawnQueue.length === 0) {
                this.debugManager.log(`Low population detected (${this.npcs.size}/${this.grid.size}). Spawning a new NPC to encourage activity.`);
                this.spawnNpc();
            }
        }

        // Handle global manager tasks after all NPCs have been processed.
        this._processRespawnQueue();
        if (this.npcTypeConfig.isHuman) {
            this._checkForNpcInteractions();
        }

        let elementClass = this.npcTypeConfig.type.toLowerCase();
        if (this.preferencesManager.getPreference('enable3dEffects', true)) {
            elementClass += ` ${elementClass}-3d`;
        }

        this.ui.updateOverlayEntities(this.npcs, this.npcTypeConfig.overlay, this.npcTypeConfig.overlayPrefix, elementClass, this.npcTypeConfig.isHuman);
    }

    /**
     * Processes the logic for a single NPC for one tick.
     * @param {Character} npc The NPC to process.
     * @private
     */
    _processSingleNpc(npc) {
        // If this NPC is being hovered over, pause its logic.
        if (this.game.hoveredNpc && this.game.hoveredNpc.npcId === npc.id) {
            npc.updateCooldowns(); // Still process cooldowns so it doesn't get stuck.
            return;
        }

        // If this NPC is under player control, skip all AI-driven logic.
        if (this.game.isNpcControlMode && this.game.controlledNpcId === npc.id) {
            npc.update(); // Still process basic updates like cooldowns.
            return;
        }

        // Before any logic, check if the NPC is on the grid and increment the counter.
        if (this.grid.isPlayable(npc.position.r, npc.position.c)) {
            this.npcsInPlayableGridCount++;
        }

        // Step 1: Handle Removal. If the NPC is fading out, do nothing else.
        if (this._handleNpcRemoval(npc)) {
            return;
        }

        // Step 2: Capture the NPC's state BEFORE any changes this tick.
        const oldPos = { ...npc.position };
        const oldConfigKey = npc.config.key;
        const wasAtPathEnd = npc.movement.path.length === 1;

        // Step 3: Update the NPC's core state (move along path, decrement cooldowns, age).
        npc.update();

        // Step 4: Determine if the NPC actually moved to a new cell.
        const hasMoved = oldPos.r !== npc.position.r || oldPos.c !== npc.position.c;

        // Step 5: Handle all logic that depends on the movement that just occurred.
        this._updateNpcDirection(npc, oldPos);
        this._handleAging(npc);
        this._handleHunger(npc);
        this._handleCoronation(npc);

        if (hasMoved) {
            // If the NPC was at the end of its path, it has now "arrived".
            if (wasAtPathEnd) {
                this._handleNpcArrival(npc);

                // Now that it has arrived, it can make a decision about what to do next.
                if (this.npcTypeConfig.isHuman && !npc.state.isSwapping && !npc.state.isNew && this.grid.isPlayable(npc.position.r, npc.position.c)) {
                    let didInteract = false;
                    // NEW: Prioritize player-commanded interaction
                    if (npc.state.interactionTarget) {
                        didInteract = this._checkForSettlementInteraction(npc, npc.state.interactionTarget);
                        npc.state.interactionTarget = null; // Clear the target after the interaction attempt
                    } else {
                        didInteract = this._checkForSettlementInteraction(npc);
                    }
                    if (!didInteract) { // If no interaction happened, then decide on a free move.
                        this._handleFreeMoveDecision(npc);
                    }
                }
            }
        }

        // Step 6: Update status flags (like `isNew` or `isMarkedForRemoval`) based on the new position.
        this._updateNpcStatus(npc);

        // Step 7: NOW, after all logic for the current tick is done, find a new path if the NPC needs one.
        this._handleNpcPathfinding(npc);
    }
    /**
     * Gets the number of NPCs currently on the playable grid.
     * @returns {number} The count of NPCs on the grid.
     */
    getNpcCount() {
        // Returns the total number of NPCs managed, including those in the buffer.
        return this.npcs.size;
    }


    /**
     * Checks if an NPC is marked for removal and initiates the fade-out process.
     * @param {Character} npc The NPC to check.
     * @returns {boolean} True if the NPC is being removed, false otherwise.
     * @private
     */
    _handleNpcRemoval(npc) {
        if (npc.state.isMarkedForRemoval) {
            npc.state.isFadingOut = true;
            setTimeout(() => this._removeAndRespawnNpc(npc.id), 500); // 0.5s fade out
            return true; // Start fading, skip further processing for this tick.
        }
        return false;
    }

    /**
     * Handles the logic for when an NPC arrives at its target destination.
     * @param {Character} npc The NPC that has arrived.
     * @private
     */
    _handleNpcArrival(npc) {
        // An NPC "arrives" when it takes the final step of its path.
        if (npc.movement.targetEffectId) {
        this.ui.clearTemporaryEffectById(npc.movement.targetEffectId);
        npc.movement.targetPos = null;
        npc.movement.targetEffectId = null;
        }
        this.debugManager.log(`NPC ${npc.id} reached target at (${npc.position.r}, ${npc.position.c}).`);
    }

    /**
     * Toggles the paused state of the NPC manager.
     */
    togglePause() {
        this.isPaused = !this.isPaused;
        this.debugManager.log(`NPCs for ${this.npcTypeConfig.type} are now ${this.isPaused ? 'paused' : 'resumed'}.`);
    }

    /**
     * Sets the paused state of the NPC manager.
     * @param {boolean} shouldBePaused - True to pause, false to resume.
     */
    setPause(shouldBePaused) {
        if (this.isPaused === shouldBePaused) return;
        this.isPaused = shouldBePaused;
        this.debugManager.log(`NPCs for ${this.npcTypeConfig.type} are now ${this.isPaused ? 'paused' : 'resumed'}.`);
    }

    /**
     * Gets the current paused state of the NPC manager.
     * @returns {boolean} True if the manager is paused.
     */
    getIsPaused() {
        return this.isPaused;
    }

    /**
     * Gets a random valid grid position for an NPC.
     * @returns {{r: number, c: number}|null} A random grid position, or null if grid is not set.
     */
    getRandomGridPosition() {
        if (!this.grid) return null;
        const r = Math.floor(Math.random() * this.grid.size);
        const c = Math.floor(Math.random() * this.grid.size);
        // Basic check to ensure it's a valid cell, could be improved
        if (this.grid.get(r, c) !== undefined) {
            return { r, c };
        }
        return null;
    }

    /**
     * Placeholder for making a random decision when an NPC moves freely.
     * This could be used for actions like eating food, using a tool, etc.
     * @param {Character} npc - The NPC that has just moved.
     * @private
     */
    _handleFreeMoveDecision(npc) {
        // An NPC cannot start a new action if they are already performing one.
        if (npc.state.currentAction) return;

        // --- Zany Action Check ---
        // First, check if the NPC should do something weird and unpredictable.
        if (Math.random() < this.globalProbability) {
            if (this._tryPerformRandomAction(npc)) return; // If a zany action was performed, we're done.
        }

        if (npc.inventory.accessory) {
            // --- Actions with items ---
            const itemType = npc.inventory.accessory.type;
            if (itemType && npcActionsConfig[itemType]) {
                const actionConfig = npcActionsConfig[itemType];
                if (Math.random() < actionConfig.probability) {
                    this._startNpcAction(npc, actionConfig);
                }
            }
        } else {
            // --- Idle actions (empty-handed) ---
            for (const actionConfig of npcIdleActionsConfig) {
                if (Math.random() < actionConfig.probability) {
                    this._startNpcAction(npc, actionConfig);
                    break; // Perform the first action that passes its probability check and stop.
                }
            }
        }
    }

    /**
     * Attempts to perform a random "zany" action based on the configuration.
     * @param {Character} npc The NPC to perform the action.
     * @returns {boolean} True if a zany action was performed, false otherwise.
     * @private
     */
    _tryPerformRandomAction(npc) {
        let random = Math.random();
        let zanyActions = npcRandomActionsConfig;
        if(!zanyActions) return false;
        if(!zanyActions.actions) return false;
        if(zanyActions.actions.length === 0) return false;

        for (const action of zanyActions.actions) {
            if (random < action.probability) {
                this.debugManager.log(`NPC ${npc.id} is performing zany action: ${action.key}`);
                npc.state.isBehavingZanily = true;
                switch (action.type) {
                    case 'effect':
                        this._performZanyEffect(npc, action);
                        return true;
                    case 'movement':
                        this._performZanyMovement(npc, action);
                        return true;
                }
            }
            random -= action.probability;
        }
        return false;
    }

    /**
     * Executes a zany "effect" action (e.g., showing a random emotion).
     * @param {Character} npc The NPC.
     * @param {object} actionConfig The configuration for the zany action.
     * @private
     */
    _performZanyEffect(npc, actionConfig) {
        // Only show an emoji effect if there are effects defined for the action.
        if (actionConfig.effects && actionConfig.effects.length > 0) {
            const randomEffectKey = actionConfig.effects[Math.floor(Math.random() * actionConfig.effects.length)];
            this.ui.addTemporaryEffect(npc.position.r, npc.position.c, randomEffectKey, { duration: actionConfig.duration });
        }
        
        // Occupy the NPC for the duration of the effect so it doesn't do anything else.
        npc.state.currentAction = actionConfig.key;
        // Clear any previous action timeout before setting a new one.
        if (npc.state.actionTimeoutId) clearTimeout(npc.state.actionTimeoutId);
        npc.state.actionTimeoutId = setTimeout(() => {
            npc.state.currentAction = null;
            npc.state.isBehavingZanily = false; // Zany action is over
            npc.state.actionTimeoutId = null;
        }, actionConfig.duration);
    }

    /**
     * Executes a zany "movement" action (e.g., zig-zagging).
     * @param {Character} npc The NPC.
     * @param {object} actionConfig The configuration for the zany action.
     * @private
     */
    _performZanyMovement(npc, actionConfig) {
        if (actionConfig.key === 'square_dance') {
            const path = this._generateSquareDancePath(npc.position, actionConfig.repeats);
            if (path && path.length > 0) {
                npc.setPath(path);
            }
            return; // Exit after handling this specific movement
        }

        if (actionConfig.key === 'fast_move') {
            // Apply a temporary effect to the NPC to increase its speed.
            npc.temporaryEffects.set('fast_move', { ...actionConfig });
            this.ui.addTemporaryEffect(npc.position.r, npc.position.c, 'dust', { duration: 5000 });
        }

        const newTarget = this.getRandomGridPosition();
        if (!newTarget) return;

        let path = this.grid.findPath(npc.position, newTarget, this.obstacleManager);
        if (!path || path.length < 2) return;

        if (actionConfig.key === 'zig_zag_path') {
            // For a zig-zag, we can just find a new random target and let the normal pathfinding handle it.
            // The "zany" part is that it's an immediate, short, random path instead of a purposeful one.
            path = path.slice(0, Math.min(path.length, actionConfig.segments + 1));
        }
        
        npc.setPath(path);
    }

    /**
     * Generates a path for an NPC to move in a small square pattern multiple times.
     * @param {{r: number, c: number}} startPos - The starting position of the NPC.
     * @param {number} repeats - The number of times to repeat the square movement.
     * @returns {Array<{r: number, c: number}>|null} The generated path, or null if not possible.
     * @private
     */
    _generateSquareDancePath(startPos, repeats) {
        const { r, c } = startPos;
        const path = [];

        // Define the 4 corners of the 2x2 square relative to the start
        const p1 = { r: r, c: c + 1 };     // Right
        const p2 = { r: r - 1, c: c + 1 }; // Up-Right
        const p3 = { r: r - 1, c: c };     // Up
        const p4 = { r: r, c: c };         // Back to start

        const squareSteps = [p1, p2, p3, p4];

        // Check if all points of the square are valid playable cells
        if (squareSteps.some(p => !this.grid.isPlayable(p.r, p.c))) {
            this.debugManager.log(`NPC at (${r},${c}) cannot perform square dance; path is obstructed.`);
            return null;
        }

        // Repeat the square path the specified number of times
        for (let i = 0; i < repeats; i++) {
            path.push(...squareSteps);
        }

        return path;
    }

    /**
     * Initiates an action for an NPC.
     * @param {Character} npc The NPC performing the action.
     * @param {object} actionConfig The configuration for the action.
     * @private
     */
    _startNpcAction(npc, actionConfig) {
        npc.state.currentAction = actionConfig.actionName;
        const itemName = npc.inventory.accessory ? ` with their ${npc.inventory.accessory.name}` : '';
        this.debugManager.log(`NPC ${npc.id} (${npc.config.name}) started action '${actionConfig.actionName}'${itemName}.`);

        this.ui.addTemporaryEffect(npc.position.r, npc.position.c, actionConfig.effect);

        setTimeout(() => {
            npc.state.currentAction = null;
        }, actionConfig.duration);
    }

    /**
     * Updates status flags for an NPC, like `isNew` or `isMarkedForRemoval`.
     * @param {Character} npc The NPC to update.
     * @private
     */
    _updateNpcStatus(npc) {
        // If an NPC that was new has now entered the playable grid, it's no longer considered new.
        if (npc.state.isNew && this.grid.isPlayable(npc.position.r, npc.position.c)) {
            // The NPC has successfully entered the grid, so it is no longer "new".
            npc.state.isNew = false;
            this.debugManager.log(`NPC ${npc.id} has entered the grid.`);
        }

        // If the NPC is in the buffer, it has "walked off", unless it's a new NPC just spawning in.
        // Babies and Royalty should never be removed this way.
        const canBeRemoved = !npc.config.isBaby && !npc.config.isRoyal;

        if (!this.grid.isPlayable(npc.position.r, npc.position.c) && !npc.state.isNew && canBeRemoved) {
            npc.state.isMarkedForRemoval = true;
        }
    }

    /**
     * Checks if an NPC has moved onto a settlement and triggers a placeholder interaction.
     * Returns true if an interaction occurred, false otherwise.
     * @param {Character} npc - The NPC to check.
     * @param {import('../models/Settlement.js').Settlement} [forcedSettlement=null] - A specific settlement to interact with, bypassing location check.
     * @private
     * @returns {boolean}
     */
    _checkForSettlementInteraction(npc, forcedSettlement = null) {
        // Non-human NPCs do not interact with settlements.
        if (!this.npcTypeConfig.isHuman) {
            return false;
        }

        // Use the forced settlement if provided, otherwise find one at the NPC's current location.
        const settlement = forcedSettlement || this.settlementManager.getSettlementAt(npc.position.r, npc.position.c);

        if (settlement) {
            // Delegate the interaction logic to the SettlementManager
            this.settlementManager.handleNpcInteraction(npc, settlement);
            return true; // An interaction was attempted.
        }
        return false;
    }

    /**
     * Spawns a new "baby" NPC near the parent NPCs.
     * @param {Character} parent1 - The first parent NPC.
     * @param {Character} parent2 - The second parent NPC.
     * @private
     */
    _spawnBabyNpc(parent1, parent2) {
        // Check population cap before spawning a baby.
        const maxNpcs = Math.floor((this.grid.playableSize * this.grid.playableSize) / 2);
        if (this.npcs.size >= maxNpcs) {
            this.debugManager.log(`Baby spawn blocked: population cap of ${maxNpcs} reached.`);
            return;
        }

        const babyConfigs = Object.values(this.masterItemsConfig.npcs).filter(npc => npc.isBaby);
        if (babyConfigs.length === 0) {
            this.debugManager.log('Attempted to spawn a baby, but no "isBaby" NPCs are configured.');
            return;
        }

        const babyConfig = babyConfigs[Math.floor(Math.random() * babyConfigs.length)];

        // Find an empty spot near the parents
        const midPointR = Math.round((parent1.position.r + parent2.position.r) / 2);
        const midPointC = Math.round((parent1.position.c + parent2.position.c) / 2);

        let spawnPos = this.grid.findEmptyNeighbors(midPointR, midPointC)[0];
        if (!spawnPos) {
            spawnPos = this.grid.findEmptyNeighbors(parent1.position.r, parent1.position.c)[0];
        }
        if (!spawnPos) {
            this.debugManager.log('Could not find an empty spot to spawn a baby NPC.');
            return;
        }

        const npc = this._createNpc(this.nextNpcId++, babyConfig, spawnPos);
        this.npcs.set(npc.id, npc);
        this.debugManager.log(`A new baby, ${npc.config.name}, was born to NPCs ${parent1.id} and ${parent2.id}!`);
    }

    /**
     * Creates a new NPC instance.
     * @param {number} id - The unique ID for the NPC.
     * @param {object} config - The configuration object for the NPC.
     * @param {{r: number, c: number}} position - The initial position of the NPC.
     * @returns {Character} The new NPC instance.
     * @private
     */
    _createNpc(id, config, position) {
        const dependencies = { id, config, position };
        const npc = new this.npcClass(dependencies);
        // OPTIMIZATION: Don't create a mutable copy. Reference the original config.
        // Instance-specific modifications (like skin tone) will be handled separately.
        npc.config = config;
        npc.display = config.display; // Store a mutable display property.
        npc.attributes.age = 0; // Initialize age for a new NPC
        npc.state.isNew = true; // Mark the NPC as new

        // Assign a random nickname based on gender
        if (this.npcTypeConfig.isHuman) {
            if (config.gender === 'male' && nicknamesConfig.male.length > 0) {
                npc.attributes.nickname = nicknamesConfig.male[Math.floor(Math.random() * nicknamesConfig.male.length)];
            } else if (config.gender === 'female' && nicknamesConfig.female.length > 0) {
                npc.attributes.nickname = nicknamesConfig.female[Math.floor(Math.random() * nicknamesConfig.female.length)];
            }
        }

        // If it's a human NPC, assign a random accessory and skin tone
        if (this.npcTypeConfig.isHuman) {
            // Skin Tone (only for supported emojis)
            if (npc.config.supportsSkinTone) {
                const skinTone = skinTones[Math.floor(Math.random() * skinTones.length)];
                npc.attributes.skinTone = skinTone;
                npc.display += skinTone;
            }

            // Accessory
            if (this.masterItemsConfig.accessories) {
                const demoSpawns = this.gameConfig.demoNpcSpawns || [];
                const npcIndex = this.nextNpcId - 1; // 0-based index

                if (this.isDemoRunning && npcIndex < demoSpawns.length) {
                    const accessoryKey = demoSpawns[npcIndex];
                    npc.inventory.accessory = this.masterItemsConfig.accessories[accessoryKey];
                } else {
                    // Check if any settlement has items to export
                    const settlementWithExports = this.settlementManager.findSettlementWithExports();

            if (settlementWithExports && Math.random() > this.gameConfig.mechanics.npcSpawnWithItemChanceIfJobsExist) {
                        // If there's work to be done, spawn the NPC empty-handed.
                        npc.inventory.accessory = null;
                        // Assign the 'distributor' role so it knows to find an export.
                        npc.state.role = 'distributor';
                        this.debugManager.log(`NPC ${npc.id} spawned empty-handed as there are items to collect.`);
                    } else {
                // If no exports are available, OR if the random chance allows it, spawn with a random item.
                        const spawnableAccessories = this._getSpawnableAccessories();
                        if (spawnableAccessories.length > 0) {
                            npc.inventory.accessory = spawnableAccessories[Math.floor(Math.random() * spawnableAccessories.length)];
                            npc.state.role = 'hauler'; // It has an item, so it's a hauler.
                    if (settlementWithExports) {
                        this.debugManager.log(`NPC ${npc.id} spawned with an item due to random chance, despite available jobs.`);
                    } else {
                        this.debugManager.log(`NPC ${npc.id} spawned with an item as there were no jobs.`);
                    }
                        }
                    }
                }
                npc.inventory.accessorySide = Math.random() < 0.5 ? 'left' : 'right';
            }
        }

        return npc;
    }

    /**
     * Determines which accessories are allowed to spawn on new NPCs.
     * The rule is: an item is spawnable UNLESS it is an "export" from a settlement.
     * This can be overridden by setting `overrideSpawn: true` on an accessory.
     * @returns {Array<object>} A list of spawnable accessory configurations.
     * @private
     */
    _getSpawnableAccessories() {
        const allExportKeys = new Set();
        const settlements = Object.values(this.masterItemsConfig.settlements);

        for (const settlement of settlements) {
            if (settlement.recipes) {
                for (const recipe of settlement.recipes) {
                    if (recipe.exports) {
                        for (const exportKey of Object.keys(recipe.exports)) {
                            allExportKeys.add(exportKey);
                        }
                    }
                }
            }
        }

        return Object.values(this.masterItemsConfig.accessories).filter(acc => {
            return acc.overrideSpawn === true || !allExportKeys.has(acc.key);
        });
    }

    /**
     * Updates the facing direction of an NPC based on its movement.
     * @param {Character} npc - The NPC to update.
     * @param {{r: number, c: number}} oldPos - The NPC's position before the update.
     * @private
     */
    _updateNpcDirection(npc, oldPos) {
        if (npc.position.c > oldPos.c) { // Moved right
            npc.movement.direction = 'right';
        } else if (npc.position.c < oldPos.c) { // Moved left
            npc.movement.direction = 'left';
        }
    }

    /**
     * Handles pathfinding for an NPC if it needs a new path.
     * @param {Character} npc - The NPC that might need a new path.
     * @private
     */
    _handleNpcPathfinding(npc) {
        if (!npc.needsNewPath()) return;

        // If the NPC is new, its first goal is to move towards the center.
        if (npc.state.isNew) {
            const newTarget = this.grid.getRandomPlayableCell();
            if (newTarget) {
                const path = this.grid.findPath(npc.position, newTarget, this.obstacleManager);
                if (path) {
                    npc.setPath(path);
                    npc.movement.targetPos = newTarget;
                    if (this.gameConfig.mechanics.isNpcTargetingEnabled) {
                        npc.movement.targetEffectId = this.ui.addTemporaryEffect(newTarget.r, newTarget.c, 'npcTarget');
                    }
                    this.debugManager.log(`NPC ${npc.id} is new, targeting playable cell (${newTarget.r}, ${newTarget.c}).`);
                }
            }
            return; // Skip the leaving logic for this turn
        }

        this._decideIfNpcShouldLeave(npc);
        let newTarget;
        if (npc.state.isLeaving) {
            // Target a random cell inmoer zone to "walk off"
            newTarget = this._getRandomBufferPosition();
        } else {
            // Regular movement within the grid area.
            if (npc.state.role === 'distributor') {
                // Empty-handed and looking for an export to pick up.
                const targetSettlement = this.settlementManager.findSettlementWithExports(npc.position, npc.memory.justVisitedWarehouse);
                if (targetSettlement) {
                    newTarget = targetSettlement.position;
                    this.debugManager.log(`Distributor NPC ${npc.id} is targeting ${targetSettlement.name} for pickup.`);
                } else {
                    // No exports available, wander randomly for now.
                    newTarget = this.getRandomGridPosition();
                    this.debugManager.log(`Distributor NPC ${npc.id} (${npc.nickname}) found no exports, wandering.`);
                }
            } else if (npc.state.role === 'consumer') {
                // Hungry and looking for food.
                const foodSource = this.settlementManager.findSettlementWithItemType('food', npc.position);
                if (foodSource) {
                    newTarget = foodSource.position;
                    this.debugManager.log(`Consumer NPC ${npc.id} is targeting ${foodSource.name} for food.`);
                } else {
                    newTarget = this.getRandomGridPosition(); // No food found, wander sadly.
                }
            } else if (npc.state.role === 'hauler' && npc.inventory.accessory) {
                // A "hauler" is carrying an item and needs to find a drop-off.
                const targetSettlement = this.settlementManager.findSettlementNeedingItem(npc.inventory.accessory.key, npc.position);
                if (targetSettlement) {
                    newTarget = targetSettlement.position;
                    this.debugManager.log(`Hauler NPC ${npc.id} with ${npc.inventory.accessory.name} is targeting ${targetSettlement.name} for drop-off.`);
                } else {
                    // No settlement needs this item; wander randomly until one does.
                    newTarget = this.getRandomGridPosition();
                    this.debugManager.log(`Hauler NPC ${npc.id} with ${npc.inventory.accessory.name} found no drop-off, wandering.`);
                }
            } else {
                // 'wandering' role and empty-handed. Just wander.
                newTarget = this.getRandomGridPosition();
            }
        }

        if (newTarget) {
            const path = this.grid.findPath(npc.position, newTarget, this.obstacleManager);
            if (path) {
                // Clear the old signpost before setting a new one
                if (npc.movement.targetEffectId) {
                    this.ui.clearTemporaryEffectById(npc.movement.targetEffectId);
                }
                npc.movement.targetPos = newTarget;
                if (this.gameConfig.mechanics.isNpcTargetingEnabled) {
                    npc.movement.targetEffectId = this.ui.addTemporaryEffect(newTarget.r, newTarget.c, 'npcTarget');
                }
                npc.setPath(path);
                this.debugManager.log(`${this.npcTypeConfig.type} ${npc.id} (${npc.attributes.nickname || npc.config.name}) found new path to (${newTarget.r}, ${newTarget.c}).`);
            }
        }
    }

    /**
     * Contains all logic for an NPC to decide whether to leave the grid.
     * An NPC will decide to leave if it is empty-handed at the edge of the map.
     * Babies and Royalty are exempt and will never leave.
     * @param {Character} npc The NPC to check.
     * @private
     */
    _decideIfNpcShouldLeave(npc) {
        // Don't re-evaluate if the NPC is already leaving.
        if (npc.state.isLeaving) return;

        // These NPCs should never leave the grid.
        if (npc.config.isBaby || npc.config.isRoyal) {
            npc.state.isLeaving = false;
            return;
        }

        // An empty-handed NPC at the edge of the playable area might decide to leave.
        if (!npc.inventory.accessory && this._isAtEdge(npc.position.r, npc.position.c) && Math.random() < this.LEAVE_CHANCE) {
            npc.state.isLeaving = true;
            this.debugManager.log(`${this.npcTypeConfig.type} ${npc.id} decided to leave.`);
        }
    }

    /**
     * Checks if a given coordinate is at the edge of the playable grid.
     * @param {number} r - The row index.
     * @param {number} c - The column index.
     * @returns {boolean} True if the cell is at the edge.
     * @private
     */
    _isAtEdge(r, c) {
        return r === this.grid.bufferSize || r === this.grid.size - this.grid.bufferSize - 1 ||
               c === this.grid.bufferSize || c === this.grid.size - this.grid.bufferSize - 1;
    }

    /**
     * Removes an NPC from the grid and queues a new one to respawn.
     * @param {number} npcId - The ID of the NPC to remove.
     * @private
     */
    _removeAndRespawnNpc(npcId) {
        if (this.npcs.has(npcId)) {
            const npc = this.npcs.get(npcId);
            // Clear any existing signpost when the NPC is removed.
            if (npc.movement.targetEffectId) {
                this.ui.clearTemporaryEffectById(npc.movement.targetEffectId);
            }

            // If the leaving NPC was in love, their partner is now single.
            if (npc.attributes.isInLoveWith !== null) {
                const partnerId = npc.attributes.isInLoveWith;
                const partner = this.npcs.get(partnerId);
                if (partner) {
                    partner.attributes.isInLoveWith = null;
                    this.debugManager.log(`NPC ${partner.id} (${partner.attributes.nickname}) is no longer in love as their partner (NPC ${npc.id}) has left.`);
                }
            }

            // --- FIX: Clear any pending action timeouts for the removed NPC ---
            if (npc.state.actionTimeoutId) {
                clearTimeout(npc.state.actionTimeoutId);
                npc.state.actionTimeoutId = null;
            }

            // Move the NPC to the ex-inhabitants pool instead of deleting.
            this.exInhabitants.set(npcId, npc);
            this.npcs.delete(npcId);
            this.respawnQueue.push({ respawnTime: Date.now() + this.RESPAWN_DELAY_MS }); // Still queue a respawn event
            this.debugManager.log(`NPC ${npcId} has left the grid and moved to ex-inhabitants. Queued for respawn.`);
        }
    }

    /**
     * Checks for interactions between NPCs, like proximity chats.
     * @private
     */
    _checkForNpcInteractions() {
        // This entire block of logic is for human NPCs only.
        if (!this.npcTypeConfig.isHuman) {
            return;
        }

        // A Set to ensure we don't process the same pair of NPCs twice in one tick.
        // This is not strictly necessary with the current loop structure but is good practice.
        const checkedPairs = new Set();

        const npcs = Array.from(this.npcs.values());
        const checked = new Set();

        // First, check for ending conversations
        for (const npc of npcs) {
            if (npc.state.isChatting && npc.state.chattingWith !== null) {
                const partner = this.npcs.get(npc.state.chattingWith);
                if (!partner || this._getDistance(npc, partner) > 1.1) {
                    npc.state.isChatting = false;
                    npc.state.chattingWith = null;
                    if (partner) {
                        partner.state.isChatting = false;
                        partner.state.chattingWith = null;
                    }
                }
            }
        }

        // Then, check for new conversations
        for (let i = 0; i < npcs.length; i++) {
            for (let j = i + 1; j < npcs.length; j++) {
                const npc1 = npcs[i];
                const npc2 = npcs[j];

                // NPCs can only interact if they are not new and are close to each other.
                if (!npc1.state.isNew && !npc2.state.isNew && !npc1.state.isChatting && !npc2.state.isChatting && this._getDistance(npc1, npc2) <= 1.1) {
                    npc1.state.isChatting = true;
                    npc1.state.chattingWith = npc2.id;
                    npc2.state.isChatting = true;
                    npc2.state.chattingWith = npc1.id;

                    this.ui.addTemporaryEffect(Math.round(npc1.position.r), Math.round(npc1.position.c), 'npcChat');
                    this.ui.addTemporaryEffect(Math.round(npc2.position.r), Math.round(npc2.position.c), 'npcChat');
                    // Randomly play a chat sound based on the configured chance
                    if (Math.random() < this.CHAT_SOUND_CHANCE) { 
                        this.audioManager.play('npc_chat', { x: (npc1.position.c + npc2.position.c) / 2, gridWidth: this.grid.size });
                    }

                    // If NPCs are already in love, they kiss!
                    if (npc1.attributes.isInLoveWith === npc2.id) {
                        this.ui.addTemporaryEffect(Math.round(npc1.position.r), Math.round(npc1.position.c), 'kiss');
                        this.ui.addTemporaryEffect(Math.round(npc2.position.r), Math.round(npc2.position.c), 'kiss');
                        this.debugManager.log(`NPCs ${npc1.id} and ${npc2.id} are in love and met up!`);
                        continue; // Skip the swap/fall-in-love logic for this pair
                    }

                   // --- Interaction Logic ---
                    // The following interactions are mutually exclusive for a single encounter.

                    // 1. Check for Accessory Swap (if both have items)
                    const canSwap = npc1.inventory.accessory && npc2.inventory.accessory && !npc1.state.isSwapping && !npc2.state.isSwapping && !npc1.state.isNew && !npc2.state.isNew;
                    if (canSwap && Math.random() < this.SWAP_CHANCE) {
                        this._handleAccessorySwap(npc1, npc2);
                        continue; // Interaction happened, move to next pair
                    }

                    // 2. Check for Play Fight (if both have weapons)
                    const canPlayFight = npc1.inventory.accessory?.type === 'weapon' && npc2.inventory.accessory?.type === 'weapon' && !npc1.state.isSwapping && !npc2.state.isSwapping && !npc1.state.isNew && !npc2.state.isNew;
                    if (canPlayFight && Math.random() < this.PLAY_FIGHT_CHANCE) {
                        this._handlePlayFight(npc1, npc2);
                        continue; // Interaction happened, move to next pair
                    }


                    // 3. Check for Gifting (if one is empty-handed)
                    const oneHasItem = (npc1.inventory.accessory && !npc2.inventory.accessory) || (!npc1.inventory.accessory && npc2.inventory.accessory);
                    const canGift = oneHasItem && !npc1.state.isSwapping && !npc2.state.isSwapping && !npc1.state.isNew && !npc2.state.isNew;
                    if (canGift && Math.random() < this.GIFT_CHANCE) {
                        this._handleAccessoryGift(npc1, npc2);
                        continue; // Interaction happened
                    }

                    // 4. Check for Falling in Love (if no item interaction occurred)
                    const canFallInLove = npc1.config.isAdult && npc2.config.isAdult && !npc1.attributes.isInLoveWith && !npc2.attributes.isInLoveWith;
                    if (canFallInLove && Math.random() < this.LOVE_CHANCE) {
                        this._handleFallingInLove(npc1, npc2);
                    }
                }
            }
        }
    }

    /**
     * Handles the logic for two NPCs swapping accessories.
     * @private
     */
    _handleAccessorySwap(npc1, npc2) {
        npc1.state.isSwapping = true;
        npc2.state.isSwapping = true;

        // Store timeout IDs on the NPCs to allow for cleanup if they are removed mid-swap.
        const outerTimeoutId = setTimeout(() => {
            this.debugManager.log(`NPCs ${npc1.id} and ${npc2.id} are swapping accessories!`);
            this.ui.animateAccessorySwap(npc1, npc2);

            const innerTimeoutId = setTimeout(() => {
                // FIX: Ensure the NPCs still exist before completing the swap.
                const n1 = this.npcs.get(npc1.id);
                const n2 = this.npcs.get(npc2.id);
                if (n1 && n2) {
                    [n1.inventory.accessory, n2.inventory.accessory] = [n2.inventory.accessory, n1.inventory.accessory];
                    [n1.inventory.accessorySide, n2.inventory.accessorySide] = [n2.inventory.accessorySide, n1.inventory.accessorySide];
                    n1.state.isSwapping = false;
                    n2.state.isSwapping = false;
                    n1.movement.path = [];
                    n2.movement.path = [];
                    this.debugManager.log(`Swap complete for NPCs ${n1.id} and ${n2.id}.`);
                } else {
                    this.debugManager.log(`Swap aborted for NPCs ${npc1.id} and ${npc2.id} as one has been removed.`);
                }
                // Clear the timeout IDs from the state.
                if (this.npcs.has(npc1.id)) npc1.state.actionTimeoutId = null;
                if (this.npcs.has(npc2.id)) npc2.state.actionTimeoutId = null;
            }, 900);

            // Store the inner timeout ID as well.
            if (this.npcs.has(npc1.id)) npc1.state.actionTimeoutId = innerTimeoutId;
            if (this.npcs.has(npc2.id)) npc2.state.actionTimeoutId = innerTimeoutId;
        }, 500);
        npc1.state.actionTimeoutId = outerTimeoutId;
        npc2.state.actionTimeoutId = outerTimeoutId;
    }

    /**
     * Handles the logic for one NPC giving an item to another.
     * @private
     */
    _handleAccessoryGift(npc1, npc2) {
        this.debugManager.log(`NPC ${npc1.id} and ${npc2.id} are considering a gift exchange.`);

        // Use a timeout to add a slight delay to the interaction, making it feel less instant.
        setTimeout(() => {
            const n1 = this.npcs.get(npc1.id);
            const n2 = this.npcs.get(npc2.id);

            if (!n1 || !n2) {
                this.debugManager.log(`Gift exchange between ${npc1.id} and ${npc2.id} aborted as one has been removed.`);
                return;
            }

            const giver = n1.inventory.accessory ? n1 : n2;
            const receiver = n1.inventory.accessory ? n2 : n1;

            this.debugManager.log(`NPC ${giver.id} is giving their ${giver.inventory.accessory.name} to NPC ${receiver.id}.`);
            receiver.inventory.accessory = giver.inventory.accessory;
            receiver.inventory.accessorySide = giver.inventory.accessorySide;
            giver.inventory.accessory = null;
            giver.state.role = 'distributor';
            giver.movement.path = []; // Force path re-evaluation
            receiver.state.role = 'hauler';
            receiver.movement.path = []; // Force path re-evaluation
        }, 500); // 0.5 second delay for the gift interaction
    }

    /**
     * Handles the logic for two NPCs engaging in a play fight.
     * @private
     */
    _handlePlayFight(npc1, npc2) {
        this.debugManager.log(`NPCs ${npc1.id} and ${npc2.id} are having a play fight with their weapons!`);

        // Set current action for both NPCs
        const actionConfig = npcActionsConfig['weapon'];
        if (actionConfig) {
            npc1.state.currentAction = actionConfig.actionName;
            npc2.state.currentAction = actionConfig.actionName;

            // Mark as chatting to prevent other interactions during the play fight
            npc1.state.isChatting = true;
            npc1.state.chattingWith = npc2.id;
            npc2.state.isChatting = true;
            npc2.state.chattingWith = npc1.id;

            // Add temporary visual effects
            this.ui.addTemporaryEffect(Math.round(npc1.position.r), Math.round(npc1.position.c), actionConfig.effect);
            this.ui.addTemporaryEffect(Math.round(npc2.position.r), Math.round(npc2.position.c), actionConfig.effect);

            // Play a sound effect (if any)
            this.audioManager.play('play_fight_sound', { x: (npc1.position.c + npc2.position.c) / 2, gridWidth: this.grid.size });

            // Force path re-evaluation after the action
            npc1.movement.path = [];
            npc2.movement.path = [];
            
            // Clear action and chatting state after duration
            setTimeout(() => {
                const n1 = this.npcs.get(npc1.id);
                const n2 = this.npcs.get(npc2.id);
                if (n1 && n2) {
                    n1.state.currentAction = null;
                    n2.state.currentAction = null;
                    n1.state.isChatting = false;
                    n1.state.chattingWith = null;
                    n2.state.isChatting = false;
                    n2.state.chattingWith = null;
                }
            }, actionConfig.duration);
        }
    }

    /**
     * Checks if an NPC is hungry and assigns the 'consumer' role if needed.
     * @param {Character} npc The NPC to check.
     * @private
     */
    _handleHunger(npc) {
        // Only non-babies who are empty-handed and not busy can get hungry.
        if (!npc.config.isBaby && !npc.inventory.accessory && !npc.state.currentAction && npc.attributes.hunger > this.HUNGER_THRESHOLD) {
            npc.state.role = 'consumer';
            this.debugManager.log(`NPC ${npc.id} is hungry and is now a consumer.`);
        }
    }
    /**
     * Handles the logic for two NPCs falling in love.
     * @private
     */
    _handleFallingInLove(npc1, npc2) {
        npc1.attributes.isInLoveWith = npc2.id;
        npc2.attributes.isInLoveWith = npc1.id;
        this.ui.addTemporaryEffect(Math.round(npc1.position.r), Math.round(npc1.position.c), 'love');
        this.ui.addTemporaryEffect(Math.round(npc2.position.r), Math.round(npc2.position.c), 'love');
        this.audioManager.play('love', { x: (npc1.position.c + npc2.position.c) / 2, gridWidth: this.grid.size });
        this.debugManager.log(`NPCs ${npc1.id} and ${npc2.id} fell in love!`);

        // Check for baby spawn after a delay, ensuring parents are still present.
        setTimeout(() => {
            if (Math.random() < this.gameConfig.mechanics.babySpawnChance) {
                const p1 = this.npcs.get(npc1.id);
                const p2 = this.npcs.get(npc2.id);
                if (p1 && p2) this._spawnBabyNpc(p1, p2);
            }
        }, 1000); // 1-second delay before checking for a baby
    }

    /**
     * Processes the respawn queue, spawning one NPC if it's time.
     * @private
     */
    _processRespawnQueue() {
        if (this.respawnQueue.length > 0 && Date.now() >= this.respawnQueue[0].respawnTime) {
            this.spawnNpc();
            this.respawnQueue.shift();
        }
    }

    /**
     * Calculates the Manhattan distance between two NPCs.
     * @param {Character} npc1 - The first NPC.
     * @param {Character} npc2 - The second NPC.
     * @returns {number} The distance.
     * @private
     */
    _getDistance(npc1, npc2) {
        return Math.abs(npc1.position.r - npc2.position.r) + Math.abs(npc1.position.c - npc2.position.c);
    }

    /**
     * Handles the aging process for an NPC.
     * @param {Character} npc The NPC to process.
     * @private
     */
    _handleAging(npc) {
        if (npc.config.isBaby && npc.attributes.age >= this.agingConfig.AGE_TO_CHILD) {
            // Age up from Baby to Boy/Girl
            const children = this.npcConfigs.filter(c => c.isChild && c.gender === npc.config.gender);
            if (children.length > 0) {
                const newConfig = children[Math.floor(Math.random() * children.length)];
                this.debugManager.log(`NPC ${npc.id} (${npc.attributes.nickname}) has aged up from a Baby to a ${newConfig.name}!`);
                npc.config = newConfig; // Change the reference to the new config
                npc.display = newConfig.display; // Reset display
                npc.state.isNew = false; // No longer a baby, can now leave the grid.
            }
        } else if (npc.config.isChild && npc.attributes.age >= this.agingConfig.AGE_TO_ADULT) {
            // Age up from Child to Adult
            const adults = this.npcConfigs.filter(c => c.isAdult && !c.isElder && !c.isRoyal && c.gender === npc.config.gender);
            if (adults.length > 0) {
                const newConfig = adults[Math.floor(Math.random() * adults.length)];
                this.debugManager.log(`NPC ${npc.id} (${npc.attributes.nickname}) has aged up from a Child to an Adult ${newConfig.name}!`);
                npc.config = newConfig; // Change the reference
                npc.display = newConfig.display; // Reset display
            }
        } else if (npc.config.isAdult && !npc.config.isElder && !npc.config.isRoyal && npc.attributes.age >= this.agingConfig.AGE_TO_ELDER) {
            // Age up from a regular Adult to an Elder
            // Ensure we are only selecting from non-elder, non-royal adults for the transition.
            const elders = this.npcConfigs.filter(c => c.isElder && c.gender === npc.config.gender);
            if (elders.length > 0) {
                const newConfig = elders[Math.floor(Math.random() * elders.length)];
                this.debugManager.log(`NPC ${npc.id} (${npc.attributes.nickname}) has aged up from a ${npc.config.name} to an Elder ${newConfig.name}!`);
                npc.config = newConfig; // Change the reference
                npc.display = newConfig.display; // Reset display
            }
        }
    }

    /**
     * Handles the coronation of an Elder NPC who acquires a crown.
     * @param {Character} npc The NPC to process.
     * @private
     */
    _handleCoronation(npc) {
        if (npc.config.isElder && npc.inventory.accessory?.key === 'accessory_crown') {
            const royalConfigKey = npc.config.gender === 'male' ? 'npc_50' : 'npc_49'; // Prince or Princess
            const royalConfig = this.npcConfigs.find(c => c.key === royalConfigKey);

            if (royalConfig) {
                this.debugManager.log(`A new royal has been crowned! NPC ${npc.id} (${npc.attributes.nickname}) is now a ${royalConfig.name}.`);
                
                // Consume the crown and transform the NPC
                npc.inventory.accessory = null;
                npc.config = royalConfig; // Change the reference
                npc.display = royalConfig.display; // Reset display

                // Trigger a grand visual effect
                this.ui.addTemporaryEffect(npc.position.r, npc.position.c, 'achievementUnlock');
            }
        }
    }

    /**
     * Gets a random position within the buffer zone.
     * @returns {{r: number, c: number}} A random buffer position.
     * @private
     */
    _getRandomBufferPosition() {
        const edge = Math.floor(Math.random() * 4); // 0: top, 1: right, 2: bottom, 3: left
        const playableStart = this.grid.bufferSize;
        const playableEnd = this.grid.size - this.grid.bufferSize - 1;
        const randomPlayableCoord = Math.floor(Math.random() * this.grid.playableSize) + playableStart;

        if (edge === 0) return { r: 0, c: randomPlayableCoord }; // Top edge
        if (edge === 1) return { r: randomPlayableCoord, c: this.grid.size - 1 }; // Right edge
        if (edge === 2) return { r: this.grid.size - 1, c: randomPlayableCoord }; // Bottom edge
        return { r: randomPlayableCoord, c: 0 }; // Left edge
    }

    /**
     * Removes an NPC at a specific grid location. Used for external actions like the delete tool.
     * @param {number} r - The row of the NPC.
     * @param {number} c - The column of the NPC.
     * @returns {boolean} True if an NPC was found and removed, false otherwise.
     */
    removeNpcAt(r, c) {
        for (const [id, npc] of this.npcs.entries()) {
            if (npc.position.r === r && npc.position.c === c) {
                this.npcs.delete(id);
                return true;
            }
        }
        return false;
    }

    /**
     * Gets the current state of all NPCs for saving.
     * @returns {object} The state object containing NPC data and the next available NPC ID.
     */
    getState() {
        const npcStates = [];
        for (const npc of this.npcs.values()) {
            npcStates.push({
                id: npc.id,
                key: npc.config.key, // Save the key, not the whole object
                pos: npc.position,
                state: { ...npc.state }, // Save a copy of the entire state object
                movement: {
                    path: npc.movement.path,
                    cooldown: npc.movement.cooldown
                },
                attributes: npc.attributes,
                inventory: { accessoryKey: npc.inventory.accessory?.key, accessorySide: npc.inventory.accessorySide },
                memory: npc.memory
            });
        }
        const exInhabitantStates = Array.from(this.exInhabitants.values()).map(npc => this._serializeNpc(npc));
        return { npcs: npcStates, exInhabitants: exInhabitantStates, nextId: this.nextNpcId, respawnQueue: this.respawnQueue };
    }

    /**
     * Restores the state of the NPC manager from a saved state.
     * @param {object} state - The saved state object.
     * @param {Array<object>} state.npcs - An array of saved NPC states.
     * @param {number} state.nextId - The next available NPC ID from the saved state.
     */
    restoreState(state) {
        this.npcs.clear();
        this.nextNpcId = state.nextId || 0;
        this.respawnQueue = state.respawnQueue || [];
        this.exInhabitants.clear();

        for (const s of state.npcs) {
            const npc = this._deserializeNpc(s);
            if (npc) this.npcs.set(npc.id, npc);
        }

        // Restore ex-inhabitants
        if (state.exInhabitants) {
            for (const s of state.exInhabitants) {
                const npc = this._deserializeNpc(s);
                if (npc) this.exInhabitants.set(npc.id, npc);
            }
        }
        // After restoring all NPCs, correctly initialize the on-grid counter.
        this.npcsInPlayableGridCount = 0;
        for (const npc of this.npcs.values()) {
            if (this.grid.isPlayable(npc.position.r, npc.position.c)) this.npcsInPlayableGridCount++;
        }
        this.debugManager.log(`NPCManager for ${this.npcTypeConfig.type} state restored.`);
    }

    /**
     * Serializes a single NPC to a state object for saving.
     * @param {Character} npc The NPC to serialize.
     * @returns {object} The serialized NPC state.
     * @private
     */
    _serializeNpc(npc) {
        return {
            id: npc.id,
            key: npc.config.key,
            pos: npc.position,
            state: { ...npc.state },
            movement: { path: npc.movement.path, cooldown: npc.movement.cooldown },
            attributes: npc.attributes,
            inventory: { accessoryKey: npc.inventory.accessory?.key, accessorySide: npc.inventory.accessorySide },
            memory: npc.memory
        };
    }

    /**
     * Deserializes an NPC state object back into a Character instance.
     * @param {object} state - The saved state object for a single NPC.
     * @returns {Character|null} The restored Character instance or null if config is not found.
     * @private
     */
    _deserializeNpc(state) {
        const config = this.npcConfigs.find(c => c.key === state.key);
        if (!config) {
            this.debugManager.log(`Could not restore NPC with key "${state.key}". Config not found.`);
            return null;
        }

        const npc = this._createNpc(state.id, config, state.pos);
        if (state.state) Object.assign(npc.state, state.state);
        if (state.movement) Object.assign(npc.movement, state.movement);
        if (state.attributes) Object.assign(npc.attributes, state.attributes);
        if (state.memory) Object.assign(npc.memory, state.memory);

        const skinTone = state.attributes?.skinTone;
        if (skinTone && npc.config.supportsSkinTone) {
            npc.display = config.display + skinTone;
        }

        const accessoryKey = state.inventory?.accessoryKey;
        if (accessoryKey && this.masterItemsConfig.accessories) {
            npc.inventory.accessory = this.masterItemsConfig.accessories[accessoryKey];
            npc.inventory.accessorySide = state.inventory?.accessorySide;
        }
        return npc;
    }
    /**
     * Updates multiple properties of an NPC in a single batch.
     * @param {number} npcId - The ID of the NPC to update.
     * @param {Array<{property: string, value: any}>} updates - An array of property-value pairs.
     */
    batchUpdateNpcProperties(npcId, updates) {
        const npc = this.npcs.get(npcId);
        if (!npc) {
            this.debugManager.log(`Attempted to batch update on non-existent NPC ID: ${npcId}`);
            return;
        }
        updates.forEach(({ property, value }) => {
            // We can reuse the single property updater for each item in the batch.
            this.updateNpcProperty(npcId, property, value);
        });
    }

    /**
     * Updates a specific property of an NPC.
     * @param {number} npcId - The ID of the NPC to update.
     * @param {string} property - The name of the property to update.
     * @param {any} value - The new value for the property.
     */
    updateNpcProperty(npcId, property, value) {
        const npc = this.npcs.get(npcId);
        if (!npc) {
            this.debugManager.log(`Attempted to update property on non-existent NPC ID: ${npcId}`);
            return;
        }

        switch (property) {
            case 'nickname':
                npc.attributes.nickname = value;
                this.debugManager.log(`Updated NPC ${npcId} nickname to "${value}".`);
                break;
            case 'character':
                const newConfig = Object.values(this.masterItemsConfig.npcs).find(c => c.key === value) || Object.values(this.masterItemsConfig.nonHumanNpcs).find(c => c.key === value);
                if (newConfig) {
                    const originalDisplay = newConfig.display; // Store the base display before modification.
                    npc.config = newConfig; // Change the reference
                    this.debugManager.log(`Changed NPC ${npcId} character to "${newConfig.name}".`);
                    // Re-apply skin tone if the new character supports it
                    if (npc.attributes.skinTone && newConfig.supportsSkinTone) {
                        npc.display = originalDisplay + npc.attributes.skinTone;
                    }
                }
                break;
            case 'age':
                npc.attributes.age = value;
                this.debugManager.log(`Updated NPC ${npcId} age to ${value}.`);
                break;
            case 'hunger':
                npc.attributes.hunger = value;
                this.debugManager.log(`Updated NPC ${npcId} hunger to ${value}.`);
                break;
            case 'role':
                npc.state.role = value;
                this.debugManager.log(`Updated NPC ${npcId} role to ${value}.`);
                break;
            case 'isLeaving':
                npc.state.isLeaving = value;
                this.debugManager.log(`Updated NPC ${npcId} isLeaving to ${value}.`);
                break;
            case 'skinTone':
                if (npc.config.supportsSkinTone) {
                    npc.attributes.skinTone = value;
                    npc.display = Object.values(this.masterItemsConfig.npcs).find(c => c.key === npc.config.key).display + value;
                    this.debugManager.log(`Updated NPC ${npcId} skin tone.`);
                }
                break;
            case 'accessory':
                if (value) {
                    const newAccessory = this.masterItemsConfig.accessories[value];
                    if (newAccessory) {
                        npc.inventory.accessory = newAccessory;
                        this.debugManager.log(`Updated NPC ${npcId} accessory to "${newAccessory.name}".`);
                    }
                } else {
                    npc.inventory.accessory = null;
                    this.debugManager.log(`Removed accessory from NPC ${npcId}.`);
                }
                break;
        }
    }

    /**
     * Deletes an NPC from the manager.
     * @param {number} npcId - The ID of the NPC to delete.
     */
    deleteNpc(npcId) {
        const npc = this.npcs.get(npcId);
        if (npc) {
            this.npcs.delete(npcId);
            this.debugManager.log(`NPC ${npcId} (${npc.attributes.nickname || npc.config.name}) was deleted via editor.`);
        }
    }

    /**
     * Toggles the 'tagged' state of an NPC.
     * @param {number} npcId - The ID of the NPC to tag.
     */
    toggleNpcTag(npcId) {
        const npc = this.npcs.get(npcId);
        if (npc) {
            // If already tagged, untag it.
            if (npc.state.taggedState !== 'none') {
                npc.state.taggedState = 'none';
            } else {
                // Otherwise, tag it as correct or incorrect.
                npc.state.taggedState = npc.state.isBehavingZanily ? 'correct' : 'incorrect';
            }
        }
    }

    /** Clears all tags from all NPCs. */
    clearAllTags() {
        for (const npc of this.npcs.values()) {
            npc.state.taggedState = 'none';
        }
    }
}